// Layered canvas manager for Hexcrawl
// Manages multiple stacked canvases for efficient rendering

export interface CanvasLayer {
  name: string;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  dirty: boolean;
}

/**
 * Manages multiple stacked canvas layers for the editor
 * Layers (bottom to top):
 * - grid: Hex grid background
 * - static: Walls, rooms (Rough.js rendered)
 * - preview: Drawing preview, selection highlights
 * - ui: Cursor, guides, measurements
 */
export class LayerManager {
  private layers: Map<string, CanvasLayer> = new Map();
  private container: HTMLElement;
  private width: number = 0;
  private height: number = 0;

  // CSS transform offset for smooth panning
  private transformX: number = 0;
  private transformY: number = 0;

  constructor(container: HTMLElement) {
    this.container = container;
    this.setupDefaultLayers();
    this.resize(container.clientWidth, container.clientHeight);

    // Handle window resize
    window.addEventListener("resize", () => {
      this.resize(this.container.clientWidth, this.container.clientHeight);
    });
  }

  private setupDefaultLayers(): void {
    this.createLayer("grid", 0);
    this.createLayer("static", 1);
    this.createLayer("preview", 2);
    this.createLayer("ui", 3);
  }

  /**
   * Create a new canvas layer
   */
  createLayer(name: string, zIndex: number): CanvasLayer {
    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.style.zIndex = String(zIndex);

    // Only the top layer should receive pointer events
    if (zIndex < 3) {
      canvas.style.pointerEvents = "none";
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error(`Failed to get 2D context for layer: ${name}`);
    }

    const layer: CanvasLayer = {
      name,
      canvas,
      ctx,
      dirty: true,
    };

    this.layers.set(name, layer);
    this.container.appendChild(canvas);

    return layer;
  }

  /**
   * Get a layer by name
   */
  getLayer(name: string): CanvasLayer | undefined {
    return this.layers.get(name);
  }

  /**
   * Mark a layer as needing redraw
   */
  markDirty(name: string): void {
    const layer = this.layers.get(name);
    if (layer) {
      layer.dirty = true;
    }
  }

  /**
   * Mark all layers as dirty
   */
  markAllDirty(): void {
    for (const layer of this.layers.values()) {
      layer.dirty = true;
    }
  }

  /**
   * Clear a layer
   */
  clearLayer(name: string): void {
    const layer = this.layers.get(name);
    if (layer) {
      layer.ctx.clearRect(0, 0, this.width, this.height);
    }
  }

  /**
   * Resize all canvases, handling HiDPI displays
   */
  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    const dpr = window.devicePixelRatio || 1;

    for (const layer of this.layers.values()) {
      this.setupCanvas(layer.canvas, layer.ctx, width, height, dpr);
      layer.dirty = true;
    }
  }

  private setupCanvas(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    dpr: number
  ): void {
    // Set actual size in memory (scaled for HiDPI)
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);

    // Set display size (CSS pixels)
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // Scale context to match HiDPI
    ctx.scale(dpr, dpr);
  }

  /**
   * Get the dimensions of the canvas
   */
  getDimensions(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  /**
   * Get the top-most canvas (for event handling)
   */
  getTopCanvas(): HTMLCanvasElement | undefined {
    return this.layers.get("ui")?.canvas;
  }

  /**
   * Apply a CSS transform to all layers for smooth panning.
   * This is much faster than re-rendering during drag.
   */
  setTransform(dx: number, dy: number): void {
    this.transformX += dx;
    this.transformY += dy;
    const transform = `translate(${this.transformX}px, ${this.transformY}px)`;
    for (const layer of this.layers.values()) {
      layer.canvas.style.transform = transform;
    }
  }

  /**
   * Get the current CSS transform offset
   */
  getTransformOffset(): { x: number; y: number } {
    return { x: this.transformX, y: this.transformY };
  }

  /**
   * Reset the CSS transform (call after re-rendering at new position)
   */
  resetTransform(): void {
    this.transformX = 0;
    this.transformY = 0;
    for (const layer of this.layers.values()) {
      layer.canvas.style.transform = "";
    }
  }
}
